import React, { useEffect, useState } from "react";
import {
    Alert, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, IconButton, MenuItem,
    Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import { getControladores, reasignarItem } from "../services/audit.service";

/**
 * AuditReasignarModal
 *
 * Props:
 *   open           {bool}
 *   onClose        {fn}
 *   onSuccess      {fn}
 *   itemId         {number}
 *   username       {string}   — quien reasigna
 *   controladorActual {string} — username del controlador actual
 */
export default function AuditReasignarModal({
    open, onClose, onSuccess,
    itemId, username, controladorActual,
}) {
    const [controladores, setControladores] = useState([]);
    const [nuevoCtrl, setNuevoCtrl] = useState("");
    const [motivo, setMotivo] = useState("");
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);
    const [errorNuevo, setErrorNuevo] = useState("");

    useEffect(() => {
        if (open) {
            getControladores()
                .then(setControladores)
                .catch(() => setError("No se pudieron cargar los controladores."));
        }
    }, [open]);

    const handleClose = () => {
        if (guardando) return;
        setNuevoCtrl("");
        setMotivo("");
        setError(null);
        setErrorNuevo("");
        onClose();
    };

    const handleGuardar = async () => {
        if (!nuevoCtrl) {
            setErrorNuevo("Seleccioná un controlador.");
            return;
        }

        setGuardando(true);
        setError(null);
        try {
            await reasignarItem(itemId, {
                username,
                nuevo_controlador_username: nuevoCtrl,
                motivo: motivo.trim() || undefined,
            });
            handleClose();
            onSuccess?.();
        } catch (e) {
            setError(
                e?.response?.data?.error ||
                "Error al reasignar. Intentá nuevamente."
            );
        } finally {
            setGuardando(false);
        }
    };

    // Nombre legible del controlador actual
    const ctrlActualNombre = controladores.find(
        (c) => c.username === controladorActual
    )?.nombre_completo || controladorActual || "Sin asignar";

    // Excluir el controlador actual del select
    const disponibles = controladores.filter(
        (c) => c.username !== controladorActual
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" gap={1}>
                        <SwapHorizIcon color="primary" />
                        <Typography variant="h6" fontWeight={700}>
                            Reasignar ítem
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={handleClose} disabled={guardando}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Controlador actual */}
                <TextField
                    fullWidth disabled
                    label="Controlador actual"
                    value={ctrlActualNombre}
                    sx={{ mb: 2 }}
                />

                {/* Nuevo controlador */}
                <TextField
                    select fullWidth
                    label="Reasignar a *"
                    value={nuevoCtrl}
                    onChange={(e) => {
                        setNuevoCtrl(e.target.value);
                        setErrorNuevo("");
                    }}
                    error={!!errorNuevo}
                    helperText={errorNuevo}
                    sx={{ mb: 2 }}
                >
                    <MenuItem value="">— Seleccioná un controlador —</MenuItem>
                    {disponibles.map((c) => (
                        <MenuItem key={c.username} value={c.username}>
                            {c.nombre_completo}
                            {c.sucursal ? (
                                <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                >
                                    ({c.sucursal})
                                </Typography>
                            ) : null}
                        </MenuItem>
                    ))}
                </TextField>

                {/* Motivo */}
                <TextField
                    fullWidth multiline rows={3}
                    label="Motivo de la reasignación"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Explicá brevemente por qué se reasigna..."
                    helperText="Opcional pero recomendado para el historial"
                />
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined" color="inherit"
                    onClick={handleClose} disabled={guardando}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    startIcon={
                        guardando
                            ? <CircularProgress size={16} color="inherit" />
                            : <SwapHorizIcon />
                    }
                    onClick={handleGuardar}
                    disabled={guardando}
                >
                    {guardando ? "Reasignando..." : "Confirmar reasignación"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}